import {
  ref as dbRef, get, onValue, push, update,
} from 'firebase/database';
import { db } from '../firebase';
import { compressImage, IMAGE_PRESETS, userFacingError } from './imageCompressionUtil';
import { roundMoney } from './currencyUtil';
import { logPosAdminEvent } from './posSubscriptionUtil';
import { isValidDateString } from './subscriptionDateUtil';
import {
  POS_ADMIN_AUDIT_ACTIONS,
  POS_EXTRA_STAFF_MAX,
  POS_SIGNUP_FIELD_MAX,
  POS_SIGNUP_RECEIPTS_NODE,
  POS_SIGNUP_REQUESTS_NODE,
  POS_SIGNUP_STATUS,
  POS_SIGNUP_TYPES,
  POS_WAREHOUSE_ADDON_MAX,
} from '../constants/posConst';

// Sign-up requests: what a VISITOR on the landing page asked for — a paid
// subscription (with proof of payment), a free trial, or a demo call for a
// custom plan.
//
// Kept apart from posSubscriptionUtil's applications because those belong to
// a client that already exists; a visitor is not a client yet. An
// administrator reads these on /admin -> Sign-up Requests and registers the
// client by hand once the payment clears.

// A deliberately loose shape check: the aim is to catch a typo'd address, not
// to re-implement RFC 5322. The real proof is the admin emailing them.
const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/**
 * Trims one text field and checks it is present and within its limit.
 *
 * @param {string} value
 * @param {string} key - a key of POS_SIGNUP_FIELD_MAX
 * @param {string} label - how the field reads in an error
 * @returns {string} the trimmed value
 * @throws {Error} user-facing, when blank or too long
 */
const requireText = (value, key, label) => {
  const clean = String(value || '').trim();
  if (!clean) throw userFacingError(`Enter your ${label}.`);
  if (clean.length > POS_SIGNUP_FIELD_MAX[key]) {
    throw userFacingError(`Keep your ${label} to ${POS_SIGNUP_FIELD_MAX[key]} characters.`);
  }
  return clean;
};

/**
 * Checks the contact details every request carries.
 *
 * @param {{name: string, business: string, email: string, phone: string}} contact
 * @returns {{name: string, business: string, email: string, phone: string}} cleaned
 * @throws {Error} user-facing, naming the first problem
 */
export const validateSignupContact = (contact) => {
  const name = requireText(contact?.name, 'name', 'full name');
  const business = requireText(contact?.business, 'business', 'business name');
  const email = requireText(contact?.email, 'email', 'email address').toLowerCase();
  if (!EMAIL_PATTERN.test(email)) throw userFacingError('Enter a valid email address.');
  const phone = requireText(contact?.phone, 'phone', 'contact number');
  return { name, business, email, phone };
};

/**
 * The plan as the request records it. COPIED, like a client's subscription,
 * so the request still reads correctly after the plan is renamed or repriced.
 *
 * @param {object} plan - a normalised plan
 * @returns {object}
 */
const planSnapshot = (plan) => ({
  planId: plan.id,
  planName: plan.name,
  planPrice: Number(plan.price) || 0,
  durationValue: plan.durationValue,
  durationUnit: plan.durationUnit,
  isCustom: plan.isCustom === true,
  freeStaff: Number(plan.freeStaff) || 0,
});

/**
 * The most staff accounts one sign-up can name: the plan's free ones, plus
 * the paid add-on cap when the plan offers add-ons.
 *
 * @param {object} plan - with `freeStaff` and `allowAddons`
 * @returns {number}
 */
export const signupStaffLimit = (plan) => {
  const free = Math.max(0, Math.floor(Number(plan?.freeStaff) || 0));
  return free + (plan?.allowAddons ? POS_EXTRA_STAFF_MAX : 0);
};

/**
 * How many of a sign-up's staff accounts are paid for: those beyond the
 * plan's free ones. The free accounts come first, exactly as on a till's
 * renewal (computeApplicationTotal).
 *
 * @param {object} plan - with `freeStaff`
 * @param {number} staffCount - how many staff accounts were named
 * @returns {number}
 */
export const signupPaidStaffCount = (plan, staffCount) => {
  const free = Math.max(0, Math.floor(Number(plan?.freeStaff) || 0));
  return Math.max(0, Math.floor(Number(staffCount) || 0) - free);
};

/**
 * Cleans and checks the staff addresses a visitor typed on the sign-up's
 * staff step. Any address is accepted — the business has no till domain yet;
 * an administrator sets the accounts up when registering the client.
 *
 * @param {string[]} emails
 * @param {object} plan - decides how many may be named (signupStaffLimit)
 * @returns {string[]} lower-cased, unique, non-blank
 * @throws {Error} user-facing, on a malformed address or too many
 */
export const cleanSignupStaffEmails = (emails, plan) => {
  const unique = [...new Set((emails || [])
    .map((value) => String(value || '').trim().toLowerCase())
    .filter(Boolean))];
  const bad = unique.find((email) => !EMAIL_PATTERN.test(email) || email.length > POS_SIGNUP_FIELD_MAX.email);
  if (bad) throw userFacingError(`"${bad}" is not a valid email address.`);
  const limit = signupStaffLimit(plan);
  if (unique.length > limit) {
    throw userFacingError(`This plan can have at most ${limit} staff account${limit === 1 ? '' : 's'}.`);
  }
  return unique;
};

/**
 * What a subscription request costs, worked out HERE rather than trusted from
 * the screen, so the stored total always matches the stored prices.
 *
 * @param {object} plan
 * @param {{extraStaff: number, warehouse: number}} addons - quantities
 * @param {{extraStaffPrice: number, warehousePrice: number}} prices - per unit
 * @returns {{extraStaffTotal: number, warehouseTotal: number, total: number}}
 */
export const computeSignupTotal = (plan, addons, prices) => {
  const extraStaffTotal = roundMoney((Number(addons?.extraStaff) || 0) * (Number(prices?.extraStaffPrice) || 0));
  const warehouseTotal = roundMoney((Number(addons?.warehouse) || 0) * (Number(prices?.warehousePrice) || 0));
  return {
    extraStaffTotal,
    warehouseTotal,
    total: roundMoney((Number(plan?.price) || 0) + extraStaffTotal + warehouseTotal),
  };
};

/**
 * Sends a visitor's paid subscription request, with the receipt, for review.
 *
 * The request and its receipt go in ONE multi-path update, so there is never a
 * request without its receipt or a receipt nobody can find.
 *
 * @param {object} details
 * @param {object} details.plan - a normalised, non-custom plan
 * @param {object} details.contact - see validateSignupContact
 * @param {{extraStaff: number, warehouse: number}} details.addons
 * @param {{extraStaffPrice: number, warehousePrice: number}} details.prices
 * @param {string} details.payApp - the app they said they paid with
 * @param {File} details.receiptFile
 * @param {string[]} [details.staffEmails] - from the staff step. When given,
 *   the paid staff count is worked out from it (accounts beyond the plan's
 *   free ones) rather than taken from `addons`.
 * @returns {Promise<object>} the saved request, with its `id` — what
 *   sendSignupReceivedEmail takes
 * @throws {Error} user-facing, on a missing plan, bad details or no receipt
 */
export const submitSubscribeRequest = async ({
  plan, contact, addons, prices, payApp, receiptFile, staffEmails,
}) => {
  if (!plan?.id || plan.isCustom) throw userFacingError('Choose a subscription plan first.');
  if (!receiptFile) throw userFacingError('Upload a photo or screenshot of your payment receipt.');
  const clean = validateSignupContact(contact);
  const staff = Array.isArray(staffEmails) ? cleanSignupStaffEmails(staffEmails, plan) : null;

  const requestedExtra = staff ? signupPaidStaffCount(plan, staff.length) : Number(addons?.extraStaff);
  const extraStaff = Math.min(POS_EXTRA_STAFF_MAX, Math.max(0, Math.floor(requestedExtra || 0)));
  const warehouse = Math.min(POS_WAREHOUSE_ADDON_MAX, Math.max(0, Math.floor(Number(addons?.warehouse) || 0)));
  const { extraStaffTotal, warehouseTotal, total } = computeSignupTotal(plan, { extraStaff, warehouse }, prices);

  // RECEIPT preset: large enough to read a reference number off, small enough
  // that the admin screen loads it quickly.
  const { dataUrl } = await compressImage(receiptFile, IMAGE_PRESETS.RECEIPT);

  const id = push(dbRef(db, POS_SIGNUP_REQUESTS_NODE)).key;
  const request = {
    type: POS_SIGNUP_TYPES.SUBSCRIBE,
    status: POS_SIGNUP_STATUS.PENDING,
    ...clean,
    ...planSnapshot(plan),
    // Who the business wants accounts for, so the administrator can set
    // them up when registering the client. The first `freeStaff` of them
    // are covered by the plan.
    ...(staff ? { staffEmails: staff } : {}),
    extraStaff,
    extraStaffPrice: Number(prices?.extraStaffPrice) || 0,
    extraStaffTotal,
    warehouse,
    warehousePrice: Number(prices?.warehousePrice) || 0,
    warehouseTotal,
    total,
    payApp: String(payApp || '').slice(0, 40),
    submittedAt: Date.now(),
  };
  await update(dbRef(db), {
    [`${POS_SIGNUP_REQUESTS_NODE}/${id}`]: request,
    [`${POS_SIGNUP_RECEIPTS_NODE}/${id}`]: dataUrl,
  });
  return { id, ...request };
};

/**
 * Sends a visitor's free-trial request for review.
 *
 * Nothing is paid, so there is no receipt and no add-ons — just who they are
 * and which trial they asked for. An administrator still sets the account up
 * by hand, which is also where a repeat trial from the same business is
 * caught.
 *
 * @param {object} details
 * @param {object} details.plan - a normalised trial plan
 * @param {object} details.contact - see validateSignupContact
 * @returns {Promise<object>} the saved request, with its `id` — what
 *   sendSignupReceivedEmail takes
 * @throws {Error} user-facing, on a non-trial plan or bad details
 */
export const submitTrialRequest = async ({ plan, contact }) => {
  if (!plan?.id || !plan.isTrial) throw userFacingError('Choose the free trial first.');
  const clean = validateSignupContact(contact);

  const id = push(dbRef(db, POS_SIGNUP_REQUESTS_NODE)).key;
  const request = {
    type: POS_SIGNUP_TYPES.TRIAL,
    status: POS_SIGNUP_STATUS.PENDING,
    ...clean,
    ...planSnapshot(plan),
    total: 0,
    submittedAt: Date.now(),
  };
  await update(dbRef(db), { [`${POS_SIGNUP_REQUESTS_NODE}/${id}`]: request });
  return { id, ...request };
};

/**
 * Books a demo call for a custom (Enterprise) plan.
 *
 * @param {object} details
 * @param {object} details.plan - the custom plan the visitor was looking at
 * @param {object} details.contact - see validateSignupContact
 * @param {string} details.about - what the business does
 * @param {string} details.features - the custom features they want
 * @param {string} details.callDate - 'YYYY-MM-DD', the day the slot starts on
 * @param {number} details.slotStart - minutes after midnight of callDate
 * @param {string} details.slotLabel - how the slot read on screen
 * @returns {Promise<object>} the saved request, with its `id` — what
 *   sendSignupReceivedEmail takes
 * @throws {Error} user-facing, on a missing date or slot, or bad details
 */
export const submitDemoRequest = async ({
  plan, contact, about, features, callDate, slotStart, slotLabel,
}) => {
  if (!isValidDateString(callDate)) throw userFacingError('Please choose a date and a 30-minute slot first.');
  if (!Number.isInteger(slotStart) || slotStart < 0) throw userFacingError('Please choose a date and a 30-minute slot first.');
  const clean = validateSignupContact(contact);
  const cleanAbout = requireText(about, 'about', 'business description');
  const cleanFeatures = requireText(features, 'features', 'custom feature list');

  const id = push(dbRef(db, POS_SIGNUP_REQUESTS_NODE)).key;
  const request = {
    type: POS_SIGNUP_TYPES.DEMO,
    status: POS_SIGNUP_STATUS.PENDING,
    ...clean,
    ...(plan?.id ? planSnapshot(plan) : {}),
    about: cleanAbout,
    features: cleanFeatures,
    callDate,
    slotStart,
    slotLabel: String(slotLabel || '').slice(0, 60),
    total: 0,
    submittedAt: Date.now(),
  };
  await update(dbRef(db), { [`${POS_SIGNUP_REQUESTS_NODE}/${id}`]: request });
  return { id, ...request };
};

/**
 * Live list of every sign-up request, newest first. Admin-only.
 *
 * @param {(requests: object[]) => void} callback
 * @param {(error: Error) => void} [onError]
 * @returns {() => void} unsubscribe
 */
export const subscribeSignupRequests = (callback, onError) => onValue(
  dbRef(db, POS_SIGNUP_REQUESTS_NODE),
  (snapshot) => {
    const list = [];
    snapshot.forEach((child) => { list.push({ id: child.key, ...child.val() }); });
    list.sort((a, b) => (b.submittedAt || 0) - (a.submittedAt || 0));
    callback(list);
  },
  (error) => {
    console.error('Sign-up requests listener failed:', error);
    if (onError) onError(error);
  },
);

/**
 * The receipt attached to one subscription request. Admin-only.
 *
 * @param {string} requestId
 * @returns {Promise<string>} a data URL, or '' when there is none
 */
export const fetchSignupReceipt = async (requestId) => {
  const snapshot = await get(dbRef(db, `${POS_SIGNUP_RECEIPTS_NODE}/${requestId}`));
  return snapshot.exists() ? String(snapshot.val() || '') : '';
};

/**
 * Moves a request to Done, Dismissed or back to New, with an optional note,
 * and records it on the administrators' trail.
 *
 * @param {object} request - from subscribeSignupRequests
 * @param {string} status - a POS_SIGNUP_STATUS value
 * @param {string} [note] - why; kept on the request
 * @param {string} [actor]
 * @returns {Promise<void>}
 * @throws {Error} user-facing, on an unknown status or an over-long note
 */
export const setSignupRequestStatus = async (request, status, note = '', actor = '') => {
  if (!request?.id) throw userFacingError('That request has no id.');
  if (!Object.values(POS_SIGNUP_STATUS).includes(status)) throw userFacingError('Unknown request status.');
  const cleanNote = String(note || '').trim();
  if (cleanNote.length > POS_SIGNUP_FIELD_MAX.note) {
    throw userFacingError(`Keep the note to ${POS_SIGNUP_FIELD_MAX.note} characters.`);
  }

  const by = String(actor || '').trim() || 'Unknown';
  await update(dbRef(db, `${POS_SIGNUP_REQUESTS_NODE}/${request.id}`), {
    status,
    adminNote: cleanNote,
    reviewedBy: by,
    reviewedAt: Date.now(),
  });

  const action = {
    [POS_SIGNUP_STATUS.DONE]: POS_ADMIN_AUDIT_ACTIONS.SIGNUP_COMPLETED,
    [POS_SIGNUP_STATUS.DISMISSED]: POS_ADMIN_AUDIT_ACTIONS.SIGNUP_DISMISSED,
    [POS_SIGNUP_STATUS.PENDING]: POS_ADMIN_AUDIT_ACTIONS.SIGNUP_REOPENED,
  }[status];
  logPosAdminEvent({
    actor: by,
    action,
    entityId: request.id,
    summary: {
      type: request.type,
      business: request.business,
      email: request.email,
      plan: request.planName || '',
      total: Number(request.total) || 0,
      ...(cleanNote ? { note: cleanNote } : {}),
    },
  });
};
